import React, { useState, useEffect } from 'react';
import './App.css';
import { 
  Card, 
  CardContent, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormControlLabel, 
  Checkbox, 
  Box,
  ThemeProvider,
  createTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  ButtonGroup
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Map as MapIcon,
  Business as BusinessIcon,
  TableChart as TableChartIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  AccountTree as GraphIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { SceneObject, Evidence, AttributeLockState, AttributeFilters } from './types';
import { initialSceneObjects, evidenceList as initialEvidenceList, movements, cases, personColors, additionalSceneObjects, additionalEvidence } from './data/mockData';
import { getRelevantLocations } from './utils/helpers';
import Header from './components/Header';
import AttributeLock from './components/AttributeLock';
import AttributeFilter from './components/AttributeFilter';
import EvidenceSection from './components/EvidenceSection';
import MapView from './components/MapView';
import LocalView from './components/LocalView';
import TableView from './components/TableView';
import TimelineView from './components/TimelineView';
import RelationshipGraph from './components/RelationshipGraph';
import SettingsView from './components/SettingsView';
import Modal from './components/Modal';
import ApplicationTutorial from './components/ApplicationTutorial';

function App() {
  // State management
  const [sceneObjects, setSceneObjects] = useState<SceneObject[]>([...initialSceneObjects, ...additionalSceneObjects]);
  const [evidenceList, setEvidenceList] = useState<Evidence[]>([...initialEvidenceList, ...additionalEvidence]);
  const [mode, setMode] = useState<'map' | 'local' | 'table' | 'timeline' | 'graph' | 'settings'>('map');
  
  // Handle mode changes with smart defaults
  const handleModeChange = (newMode: 'map' | 'local' | 'table' | 'timeline' | 'graph' | 'settings') => {
    setMode(newMode);
    
    // Auto-select a location when switching to Local View
    if (newMode === 'local' && uniqueMapLocations.length > 0) {
      // If no location is selected or location is from previous case, select a new one
      const currentLocationValid = selectedMapLocation && uniqueMapLocations.includes(selectedMapLocation);
      
      if (!currentLocationValid) {
        // Prefer locations that have layouts
        const preferredLocations = uniqueMapLocations.filter(location => 
          location.toLowerCase().includes('marina bay sands') || 
          location.toLowerCase().includes('chinatown restaurant')
        );
        
        const defaultLocation = preferredLocations[0] || uniqueMapLocations[0];
        setSelectedMapLocation(defaultLocation);
      }
    }
  };
  const [attributeLock, setAttributeLock] = useState<AttributeLockState>({ attribute: null, value: null });
  const [attributeFilters, setAttributeFilters] = useState<AttributeFilters>({
    people: {},
    location: {},
    time: {}
  });
  const [selectedObject, setSelectedObject] = useState<SceneObject | Evidence | null>(null);
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);
  const [currentCaseId, setCurrentCaseId] = useState('case3');
  const [showMovementLines, setShowMovementLines] = useState(true);
  const [selectedMapLocation, setSelectedMapLocation] = useState<string | null>(null);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [leftPanelWidth, setLeftPanelWidth] = useState(30); // Percentage
  const [isDragging, setIsDragging] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(() => {
    // Check if this is the first time visiting
    const hasSeenTutorial = localStorage.getItem('hasSeenTutorial');
    return !hasSeenTutorial; // Show tutorial if they haven't seen it
  });

  // Time range state
  const [startTimeRange, setStartTimeRange] = useState(0);
  const [endTimeRange, setEndTimeRange] = useState(100);

  // Get all unique times for slider (only for current case)
  const allTimes = Array.from(new Set([
    ...sceneObjects.filter(obj => obj.caseId === currentCaseId).map(obj => new Date(obj.time).getTime()),
    ...evidenceList.filter(obj => obj.caseId === currentCaseId).map(obj => new Date(obj.time).getTime())
  ])).sort((a, b) => a - b);

  const earliestTime = allTimes[0] || Date.now();
  const latestTime = allTimes[allTimes.length - 1] || Date.now();
  
  // Ensure minimum time range (at least 1 hour)
  const timeRange = Math.max(latestTime - earliestTime, 60 * 60 * 1000); // 1 hour in milliseconds
  const adjustedLatestTime = earliestTime + timeRange;

  // Time conversion functions
  const getTimestampFromSlider = (value: number): number => {
    return earliestTime + (value / 100) * timeRange;
  };

  const getSliderValueFromTimestamp = (timestamp: number): number => {
    return ((timestamp - earliestTime) / timeRange) * 100;
  };

  // Initialize time range only when case changes, not when allTimes changes
  useEffect(() => {
    setStartTimeRange(0);
    setEndTimeRange(100);
  }, [currentCaseId]);

  // Get unique values for filters
  const uniquePeople = Array.from(new Set(
    sceneObjects
      .filter(obj => obj.caseId === currentCaseId)
      .flatMap(obj => obj.people)
  ))
    .map(originalName => ({
      originalName,
      displayName: originalName.replace(/^(Detective|Person of Interest|Witness|Security Guard|Victim)\s+/, '')
    }))
    .filter((person, index, array) => 
      array.findIndex(p => p.originalName === person.originalName) === index
    );

  const uniqueMapLocations = Array.from(new Set(
    sceneObjects
      .filter(obj => obj.location.mode === 'global' && obj.caseId === currentCaseId)
      .map(obj => obj.location.global?.name || 'Unknown')
  ));

  const uniqueLocalLocations = Array.from(new Set(
    sceneObjects
      .filter(obj => obj.location.mode === 'local' && obj.caseId === currentCaseId)
      .map(obj => obj.location.local?.name || 'Unknown')
  ));

  const uniqueDates = Array.from(new Set(
    sceneObjects
      .filter(obj => obj.caseId === currentCaseId)
      .map(obj => new Date(obj.time).toDateString())
  ));

  // Auto-select location when case changes and we're in Local View
  useEffect(() => {
    if (mode === 'local' && uniqueMapLocations.length > 0 && !selectedMapLocation) {
      // Prefer locations that have layouts
      const preferredLocations = uniqueMapLocations.filter(location => 
        location.toLowerCase().includes('marina bay sands') || 
        location.toLowerCase().includes('chinatown restaurant')
      );
      
      const defaultLocation = preferredLocations[0] || uniqueMapLocations[0];
      setSelectedMapLocation(defaultLocation);
    }
  }, [currentCaseId, mode, uniqueMapLocations, selectedMapLocation]);

  // Event handlers
  const handleLockChange = (attribute: 'people' | 'location' | 'time' | null, value: string | null) => {
    setAttributeLock({ attribute, value });
    
    if (attribute && value) {
      const relevantItems = getRelevantLocations(attribute, value, sceneObjects, currentCaseId);
      
      // Update filters to only show relevant items
      const newFilters = { ...attributeFilters };
      
      if (attribute === 'people') {
        Object.keys(newFilters.people).forEach(person => {
          newFilters.people[person] = relevantItems.includes(person);
        });
      } else if (attribute === 'location') {
        Object.keys(newFilters.location).forEach(location => {
          newFilters.location[location] = relevantItems.includes(location);
        });
      } else if (attribute === 'time') {
        Object.keys(newFilters.time).forEach(date => {
          newFilters.time[date] = relevantItems.includes(date);
        });
      }
      
      setAttributeFilters(newFilters);
    }
  };

  const handleFilterChange = (type: 'people' | 'location' | 'time', value: string, checked: boolean) => {
    setAttributeFilters(prev => {
      const newFilters = {
        ...prev,
        [type]: {
          ...prev[type],
          [value]: checked
        }
      };

      // Smart cascading logic
      if (type === 'people') {
        if (!checked) {
          // When deselecting a person, check if any locations should be deselected
          const personLocations = new Set<string>();
          
          // Get all locations where this person has been
          sceneObjects
            .filter(obj => obj.caseId === currentCaseId && 'people' in obj && obj.people.includes(value))
            .forEach(obj => {
              const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
              personLocations.add(locationName);
            });

          // For each location this person visited, check if any other people are still selected there
          personLocations.forEach(locationName => {
            const otherPeopleAtLocation = sceneObjects
              .filter(obj => 
                obj.caseId === currentCaseId && 
                'people' in obj && 
                (obj.location.global?.name === locationName || obj.location.local?.name === locationName)
              )
              .flatMap(obj => obj.people)
              .filter(person => person !== value && newFilters.people[person] !== false);

            // If no other people are selected at this location, deselect the location
            if (otherPeopleAtLocation.length === 0) {
              newFilters.location[locationName] = false;
            }
          });
        } else {
          // When selecting a person, select all locations where this person has been
          const personLocations = new Set<string>();
          
          // Get all locations where this person has been
          sceneObjects
            .filter(obj => obj.caseId === currentCaseId && 'people' in obj && obj.people.includes(value))
            .forEach(obj => {
              const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
              personLocations.add(locationName);
            });

          // Select all locations where this person has been
          personLocations.forEach(locationName => {
            newFilters.location[locationName] = true;
          });
        }
      } else if (type === 'location') {
        if (!checked) {
          // When deselecting a location, check if any people should be deselected
          const peopleAtLocation = new Set<string>();
          
          // Get all people who have been at this location
          sceneObjects
            .filter(obj => 
              obj.caseId === currentCaseId && 
              'people' in obj && 
              (obj.location.global?.name === value || obj.location.local?.name === value)
            )
            .forEach(obj => {
              obj.people.forEach(person => peopleAtLocation.add(person));
            });

          // For each person at this location, check if they have any other locations
          peopleAtLocation.forEach(person => {
            const otherLocations = new Set<string>();
            
            sceneObjects
              .filter(obj => 
                obj.caseId === currentCaseId && 
                'people' in obj && 
                obj.people.includes(person)
              )
              .forEach(obj => {
                const locationName = obj.location.global?.name || obj.location.local?.name || 'Unknown';
                if (locationName !== value) {
                  otherLocations.add(locationName);
                }
              });

            // If this person has no other locations that are still selected, deselect the person
            const hasOtherSelectedLocations = Array.from(otherLocations).some(location => 
              newFilters.location[location] !== false
            );
            
            if (!hasOtherSelectedLocations) {
              newFilters.people[person] = false;
            }
          });
        } else {
          // When selecting a location, select all people who have been at this location
          const peopleAtLocation = new Set<string>();
          
          // Get all people who have been at this location
          sceneObjects
            .filter(obj => 
              obj.caseId === currentCaseId && 
              'people' in obj && 
              (obj.location.global?.name === value || obj.location.local?.name === value)
            )
            .forEach(obj => {
              obj.people.forEach(person => peopleAtLocation.add(person));
            });

          // Select all people who have been at this location
          peopleAtLocation.forEach(person => {
            newFilters.people[person] = true;
          });
        }
      }

      return newFilters;
    });
  };

  const handleObjectClick = (obj: SceneObject | Evidence) => {
    setSelectedObject(obj);
  };

  const handleEvidenceClick = (evidence: Evidence) => {
    setSelectedObject(evidence);
    setMode('map');
  };

  // Drag handlers for left panel
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const containerWidth = window.innerWidth;
    const newWidth = (e.clientX / containerWidth) * 100;
    
    // Constrain width between 15% and 25%
    const constrainedWidth = Math.max(15, Math.min(25, newWidth));
    setLeftPanelWidth(constrainedWidth);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const handleCaseChange = (caseId: string) => {
    setCurrentCaseId(caseId);
    setSelectedObject(null);
    setSelectedEvidence(null);
    setAttributeLock({ attribute: null, value: null });
    setAttributeFilters({ people: {}, location: {}, time: {} });
    setSelectedMapLocation(null); // Clear selected location when changing cases
  };

  const handleTimeRangeChange = (start: number, end: number) => {
    setStartTimeRange(start);
    setEndTimeRange(end);
  };

  // Get person color function
  const getPersonColor = (personName: string): string => {
    const index = uniquePeople.findIndex(p => p.originalName === personName);
    return personColors[index % personColors.length];
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#dc004e',
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <Header 
          currentCaseId={currentCaseId}
          cases={cases}
          onCaseChange={handleCaseChange}
        />
        
                <div style={{ display: 'flex', height: 'calc(100vh - 50px)', overflow: 'hidden' }}>
          {/* Left Panel - Filters */}
          {showLeftPanel && (
            <div style={{ width: `${leftPanelWidth}%`, padding: 8, overflowY: 'auto', background: '#f5f5f5', position: 'relative' }}>
              {/* Drag Handle */}
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  cursor: 'col-resize',
                  backgroundColor: 'transparent',
                  zIndex: 10
                }}
                onMouseDown={handleMouseDown}
              />
              {/* View Mode Toggle */}
              
          <AttributeLock
            attributeLock={attributeLock}
            onLockChange={handleLockChange}
            uniquePeople={uniquePeople}
            uniqueMapLocations={uniqueMapLocations}
            uniqueLocalLocations={uniqueLocalLocations}
            uniqueDates={uniqueDates}
            allTimes={allTimes.map(t => new Date(t).toISOString())}
            startTimeRange={startTimeRange}
            endTimeRange={endTimeRange}
            onTimeRangeChange={handleTimeRangeChange}
            getTimestampFromSlider={getTimestampFromSlider}
            getSliderValueFromTimestamp={getSliderValueFromTimestamp}
          />
          
          <AttributeFilter
            attributeFilters={attributeFilters}
            onFilterChange={handleFilterChange}
            uniquePeople={uniquePeople}
            uniqueMapLocations={uniqueMapLocations}
            uniqueLocalLocations={uniqueLocalLocations}
            uniqueDates={uniqueDates}
            attributeLock={attributeLock}
            getRelevantLocations={(attribute, value) => getRelevantLocations(attribute, value, sceneObjects, currentCaseId)}
            sceneObjects={sceneObjects}
            currentCaseId={currentCaseId}
          />
          
          <EvidenceSection
            evidenceList={evidenceList}
            currentCaseId={currentCaseId}
            onEvidenceClick={handleEvidenceClick}
          />
          
          <Box sx={{ mb: 1 }}>
            <Accordion defaultExpanded sx={{ boxShadow: 'none', border: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ 
                minHeight: 40, 
                backgroundColor: '#f8f9fa',
                borderBottom: 'none'
              }}>
                <Box display="flex" alignItems="center">
                  <SettingsIcon sx={{ mr: 1, color: 'primary.main', fontSize: 18 }} />
                  <Typography variant="subtitle2" sx={{ 
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#2c3e50'
                  }}>
                    Display Options
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, pb: 1 }}>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={showMovementLines}
                    onChange={(e) => setShowMovementLines(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <VisibilityIcon sx={{ mr: 0.5, fontSize: 16 }} />
                    <Typography variant="body2">Show Movement Lines</Typography>
                  </Box>
                }
                sx={{ mb: 1.5 }}
              />

              {mode === 'local' && (
                <FormControl fullWidth size="small">
                  <InputLabel>
                    <Box display="flex" alignItems="center">
                      <BusinessIcon sx={{ mr: 0.5, fontSize: 16 }} />
                      Select Map Location
                    </Box>
                  </InputLabel>
                  <Select
                    value={selectedMapLocation || ''}
                    onChange={(e) => setSelectedMapLocation(e.target.value || null)}
                    label={
                      <Box display="flex" alignItems="center">
                        <BusinessIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        Select Map Location
                      </Box>
                    }
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {uniqueMapLocations.map(location => (
                      <MenuItem key={location} value={location}>{location}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
                </AccordionDetails>
              </Accordion>
            </Box>
        </div>
      )}
          
          {/* Right Panel - Map/Local View */}
          <div style={{ width: showLeftPanel ? `${100 - leftPanelWidth}%` : '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
            {/* Toggle Left Panel Button */}
            <Box
              position="absolute"
              top={8}
              left={8}
              zIndex={1000}
              sx={{
                backgroundColor: 'white',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
              onClick={() => setShowLeftPanel(!showLeftPanel)}
            >
              {showLeftPanel ? <ChevronLeftIcon /> : <MenuIcon />}
            </Box>

            {/* View Mode Buttons - Top Right */}
            <Box
              position="absolute"
              top={8}
              right={8}
              zIndex={1000}
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                boxShadow: 2,
                display: 'flex',
                alignItems: 'center',
                padding: '2px'
              }}
            >
              <ButtonGroup variant="outlined" size="small">
                <Button
                  variant={mode === 'map' ? 'contained' : 'outlined'}
                  onClick={() => handleModeChange('map')}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <MapIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Map
                </Button>
                <Button
                  variant={mode === 'local' ? 'contained' : 'outlined'}
                  onClick={() => handleModeChange('local')}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Local
                </Button>
                <Button
                  variant={mode === 'table' ? 'contained' : 'outlined'}
                  onClick={() => handleModeChange('table')}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <TableChartIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Tabular
                </Button>
                <Button
                  variant={mode === 'timeline' ? 'contained' : 'outlined'}
                  onClick={() => handleModeChange('timeline')}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Timeline
                </Button>
                <Button
                  variant={mode === 'graph' ? 'contained' : 'outlined'}
                  onClick={() => handleModeChange('graph')}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <GraphIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Graph
                </Button>
                <Button
                  variant={mode === 'settings' ? 'contained' : 'outlined'}
                  onClick={() => handleModeChange('settings')}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    '&.MuiButton-contained': {
                      backgroundColor: 'primary.main',
                      color: 'white'
                    }
                  }}
                >
                  <SettingsIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Settings
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setTutorialOpen(true)}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    fontSize: '0.7rem',
                    border: 'none',
                    backgroundColor: '#f0f8ff',
                    '&:hover': {
                      backgroundColor: '#e6f3ff'
                    }
                  }}
                >
                  <HelpIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  Help
                </Button>
              </ButtonGroup>
            </Box>
          {mode === 'map' ? (
            <MapView
              sceneObjects={sceneObjects}
              evidenceList={evidenceList}
              movements={movements}
              currentCaseId={currentCaseId}
              mode={mode}
              attributeLock={attributeLock}
              attributeFilters={attributeFilters}
              startTimeRange={startTimeRange}
              endTimeRange={endTimeRange}
              getTimestampFromSlider={getTimestampFromSlider}
              getRelevantLocations={(attribute, value) => getRelevantLocations(attribute, value, sceneObjects, currentCaseId)}
              onObjectClick={handleObjectClick}
              showMovementLines={showMovementLines}
            />
          ) : mode === 'local' ? (
            <LocalView
              selectedMapLocation={selectedMapLocation}
            />
          ) : mode === 'table' ? (
            <TableView
              sceneObjects={sceneObjects}
              evidenceList={evidenceList}
              attributeFilters={attributeFilters}
              attributeLock={attributeLock}
              currentCaseId={currentCaseId}
              onObjectClick={handleObjectClick}
            />
          ) : mode === 'timeline' ? (
            <TimelineView
              sceneObjects={sceneObjects}
              evidenceList={evidenceList}
              movements={movements}
              currentCaseId={currentCaseId}
              attributeLock={attributeLock}
              attributeFilters={attributeFilters}
              startTimeRange={startTimeRange}
              endTimeRange={endTimeRange}
              getTimestampFromSlider={getTimestampFromSlider}
              onObjectClick={handleObjectClick}
            />
          ) : mode === 'graph' ? (
            <RelationshipGraph
              sceneObjects={sceneObjects}
              evidenceList={evidenceList}
              movements={movements}
              currentCaseId={currentCaseId}
              attributeLock={attributeLock}
              attributeFilters={attributeFilters}
              startTimeRange={startTimeRange}
              endTimeRange={endTimeRange}
              getTimestampFromSlider={getTimestampFromSlider}
              onObjectClick={handleObjectClick}
            />
          ) : mode === 'settings' ? (
            <SettingsView />
          ) : null}
        </div>
      </div>
      
      <Modal
        selectedObject={selectedObject}
        onClose={() => setSelectedObject(null)}
        sceneObjects={sceneObjects}
        evidenceList={evidenceList}
        currentCaseId={currentCaseId}
      />

      {/* Application Tutorial */}
      <ApplicationTutorial
        open={tutorialOpen}
        onClose={() => {
          setTutorialOpen(false);
          // Mark that the user has seen the tutorial
          localStorage.setItem('hasSeenTutorial', 'true');
        }}
      />
    </div>
    </ThemeProvider>
  );
}

export default App;
